import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useRedirect,
  useRefresh,
  SelectInput,
  CreateProps,
  BooleanInput,
  FormDataConsumer,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { CircularProgress, Grid, Typography } from "@material-ui/core";
import EditableDataGrid from "./components/EditableAreaDatagrid";
import { AcademicYearResources, GetParam } from "utils/tools";
import CustomCreateToolbar from "components/CustomCreateToolbar";
import { labelDataGridStyles } from "utils/style";
import moment from "moment";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import TbfCodeInput from "./components/TbfCodeInput";
import { validateRateChartCreation } from "./validation";
import CustomDateRangeInput from "../../components/CustomDateRangeInput";
import CreateWrapper from "../../components/CreateWrapper";
import PrevRateChartInputField from "./components/PrevRateChartInputField";

interface ICustomReferenceInput {
  schoolId: string;
  callback: (x: string) => void;
}

const CustomReferenceInput = (props: ICustomReferenceInput) => {
  const startedAt = useField("startedAt");
  const endedAt = useField("endedAt");
  const invoiceIssuedAt = useField("invoiceIssuedAt");

  return (
    <ReferenceInput
      label="Select Academic Year"
      source="academicYearId"
      reference="academicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(props.schoolId) }}
      onChange={({ target }) => {
        props.callback(target.value);

        startedAt.input.onChange("");
        endedAt.input.onChange("");
        invoiceIssuedAt.input.onChange("");
      }}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const RateChartCreate = (props: CreateProps) => {
  const [areas, setAreas] = useState<IArea[]>([]);
  const [rateCharts, setRateCharts] = useState<IRateChart[]>([]);
  const [idAcademicYear, setIdAcademicYear] = useState<number>();
  const [loadingPrevChart, setLoadingPrevChart] = useState(false);

  const provider = useDataProvider();
  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Create", link: "" },
  ];

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("areas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result) => {
        setAreas(result.data as IArea[]);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transform = (data: any) => {
    data.rateChartItems = [
      ...data.oneTimeAWeekPricing,
      ...data.twoTimeAWeekPricing,
      ...data.threeTimeAWeekPricing,
      ...data.fourTimeAWeekPricing,
      ...data.fiveTimeAWeekPricing,
    ];

    data.status = "active";

    delete data.oneTimeAWeekPricing;
    delete data.twoTimeAWeekPricing;
    delete data.threeTimeAWeekPricing;
    delete data.fourTimeAWeekPricing;
    delete data.fiveTimeAWeekPricing;

    return data;
  };

  const CustomRedirect = (props: { data: IRateChart }) => {
    redirect(`/schools/${props.data.schoolId}/show/5`);
    refresh();
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);

  useEffect(() => {
    if (idAcademicYear !== undefined) {
      provider
        .getList("rateCharts", {
          filter: { AcademicYearId: Number(idAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then((result) => {
          setRateCharts(result.data as IRateChart[]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  const academicYearInputCallback = (id: any) => {
    setIdAcademicYear(id);
  };

  let excludeDates: IExcludeDates[] = [];
  rateCharts.forEach((rateChart: IRateChart) => {
    let temp: IExcludeDates = {
      startDate: rateChart.startedAt,
      endDate: rateChart.endedAt,
    };

    excludeDates.push(temp);
  });

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New Regular Rate Chart"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm
          validate={(values: any) =>
            validateRateChartCreation(values, excludeDates)
          }
          initialValues={{ rateChartItems: [] }}
          toolbar={<CustomCreateToolbar />}
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput
                callback={academicYearInputCallback}
                schoolId={param.get("schoolId")}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <TbfCodeInput />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CustomDateRangeInput
                fromSource="startedAt"
                toSource="endedAt"
                fromLabel="Pricing Start Date"
                toLabel="Pricing End Date"
                parentStartDate={moment
                  .utc(academicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                fromMaxDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                toMaxDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                excludeDates={excludeDates}
                gridItemWidth={3}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date *"
                min={moment().format("Y-MM-DD")}
              />
            </Grid>

            {/* <Grid item xs={4}>
              <SelectInput
                source="isIncludeTax"
                label="Tax Included?"
                choices={[
                  { id: true, name: "Yes" },
                  { id: false, name: "No" },
                ]}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Typography variant="h6">
                Area Pricing (Exclusive of GST)
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <BooleanInput
                source="isDuplicateRateChartEnable"
                label="Enable Duplicate Ratechart"
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <FormDataConsumer>
              {({ formData }) =>
                formData.isDuplicateRateChartEnable && (
                  <SanitizedGrid
                    container
                    spacing={2}
                    style={{ marginTop: "-55px", padding: "10px" }}
                  >
                    <Grid item xs={3}>
                      <PrevRateChartInputField
                        rateChartItemSource="rateChartItems"
                        schoolId={Number(param.get("schoolId"))}
                        setLoading={setLoadingPrevChart}
                      />
                    </Grid>
                    <Grid item xs={9}></Grid>
                  </SanitizedGrid>
                )
              }
            </FormDataConsumer>

            {loadingPrevChart ? (
              <SanitizedGrid
                container
                spacing={2}
                style={{
                  padding: "10px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={100} />
              </SanitizedGrid>
            ) : (
              <SanitizedGrid container spacing={2} style={{ padding: "10px" }}>
                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={labelDataGridStyles}>
                      1 Time a Week
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <EditableDataGrid
                      areas={areas}
                      name="oneTimeAWeekPricing"
                      frequency={1}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={labelDataGridStyles}>
                      2 Time a Week
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <EditableDataGrid
                      areas={areas}
                      name="twoTimeAWeekPricing"
                      frequency={2}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={labelDataGridStyles}>
                      3 Time a Week
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <EditableDataGrid
                      areas={areas}
                      name="threeTimeAWeekPricing"
                      frequency={3}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={labelDataGridStyles}>
                      4 Time a Week
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <EditableDataGrid
                      areas={areas}
                      name="fourTimeAWeekPricing"
                      frequency={4}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={labelDataGridStyles}>
                      5 Time a Week
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <EditableDataGrid
                      areas={areas}
                      name="fiveTimeAWeekPricing"
                      frequency={5}
                    />
                  </Grid>
                </Grid>
              </SanitizedGrid>
            )}
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default RateChartCreate;
